import React, { useState, useCallback, useEffect } from 'react';
import { Button, Dropdown, Form, Icon, Input, Message, Segment, Label } from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';

const EVENT_TYPES = [
  { key: 'none', value: 'none', text: 'None (custom — no game lookup)' },
  { key: 'mlb', value: 'mlb', text: 'MLB' },
  { key: 'nfl', value: 'nfl', text: 'NFL' },
  { key: 'nba', value: 'nba', text: 'NBA' },
  { key: 'nhl', value: 'nhl', text: 'NHL' },
  { key: 'mls', value: 'mls', text: 'MLS' },
];

// Leagues we can auto-look up today's game for. Anything else (incl. 'none')
// is a fully manual/custom ticket with no ESPN lookup.
const AUTO_LOOKUP_LEAGUES = ['mlb', 'nfl', 'nba', 'nhl', 'mls'];

// Most a single batch will generate — each seat is its own paid image call, so
// cap it so a fat-fingered "1-500" range can't spin forever.
const MAX_BATCH = 12;
// One corrective "make it more perfect" pass after the first generation.
const MAX_ATTEMPTS = 2;

// A card is still working (spinner + live status) in these states.
const ACTIVE = ['queued', 'generating', 'verifying', 'refining', 'retrying'];
const FAILED = ['timeout', 'network', 'rate_limit', 'connection', 'auth', 'error'];
// Errors a short wait usually fixes on its own — auto-retried with backoff
// before we surface a failure (busy image service, dropped signal, timeout).
const RETRYABLE = ['rate_limit', 'timeout', 'connection', 'network'];
const AUTO_RETRIES = 2;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Parse a seats string into a de-duped list. Accepts commas/spaces and numeric
// ranges: "12, 13, 15-18" -> ["12","13","15","16","17","18"]. Non-numeric seats
// like "14A" pass through untouched.
const parseSeats = (raw) => {
  if (!raw || !raw.trim()) return [];
  const out = [];
  raw.split(/[,\s]+/).filter(Boolean).forEach((tok) => {
    const m = tok.match(/^(\d+)[-–](\d+)$/);
    if (m) {
      let a = parseInt(m[1], 10);
      let b = parseInt(m[2], 10);
      if (a > b) { const t = a; a = b; b = t; }
      // Expand the range, bounded so a pathological "1-99999" can't blow up.
      // The final slice(0, MAX_BATCH) caps how many actually get generated.
      for (let s = a; s <= b && out.length < 200; s++) out.push(String(s));
      return;
    }
    out.push(tok);
  });
  return [...new Set(out)].slice(0, MAX_BATCH);
};

// Turn an axios failure into a short type the UI can act on. The distinction
// that matters at a stadium: did it time out / lose signal (retry will likely
// work) vs a real server error (retry won't).
const classifyError = (err) => {
  if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) return 'timeout';
  if (err.response) {
    const t = err.response.data && err.response.data.error_type;
    if (t) return t;
    if (err.response.status === 429) return 'rate_limit';
    if (err.response.status === 504) return 'timeout';
    return 'error';
  }
  return 'network'; // request left the device but no response came back
};

const errorMessage = (type, err) => {
  switch (type) {
    case 'timeout':
      return 'Timed out — the image service was slow (common on stadium wifi/cell). Tap Retry.';
    case 'network':
      return 'No response — check your signal and tap Retry.';
    case 'connection':
      return 'Lost connection to the image service. Tap Retry.';
    case 'rate_limit':
      return 'Service is busy right now. Wait a few seconds and tap Retry.';
    case 'no_credits':
      return 'The AI account is out of credits — this needs a billing top-up, not a retry.';
    case 'auth':
      return 'Server configuration issue (auth) — not something a retry will fix.';
    default:
      return (err && err.response && err.response.data && err.response.data.error) ||
        'Something went wrong. Tap Retry.';
  }
};

function Tickets(props) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [gameInfo, setGameInfo] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  // First-time-user instructions. Shown by default; dismissible and remembered.
  const [showHelp, setShowHelp] = useState(
    () => localStorage.getItem('ticketHelpDismissed') !== '1'
  );

  const dismissHelp = () => {
    localStorage.setItem('ticketHelpDismissed', '1');
    setShowHelp(false);
  };

  // Dynamic fields from AI analysis
  const [editableFields, setEditableFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [team, setTeam] = useState('');
  const [eventType, setEventType] = useState('none');

  // Batch generation: one or more seats -> one ticket each.
  const [seatsInput, setSeatsInput] = useState('');
  const [results, setResults] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const resultsRef = React.useRef(null);
  // When a saved template with cached boxes is loaded, remember it so we can
  // offer "Re-scan boxes" (redo the read if the cached boxes came out wrong).
  const [loadedTemplateId, setLoadedTemplateId] = useState(null);
  const [rescanning, setRescanning] = useState(false);

  const autoLookup = AUTO_LOOKUP_LEAGUES.includes(eventType);
  const anyActive = results.some((r) => ACTIVE.includes(r.status));
  const anyReady = results.some((r) => r.image && (r.status === 'done' || r.status === 'warning'));

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Tick a clock while anything is generating so the elapsed timer moves and it
  // never looks frozen (important reassurance on a slow connection).
  useEffect(() => {
    if (!anyActive) return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [anyActive]);

  const fetchTemplates = async () => {
    try {
      const res = await axiosInstance.get('ticket-templates/');
      setTemplates(res.data.results || res.data);
    } catch (e) {}
  };

  const resetState = () => {
    setEditableFields([]);
    setFieldValues({});
    setAnalysis(null);
    setGameInfo(null);
    setResults([]);
    setSeatsInput('');
    setError(null);
    setDownloadStatus(null);
    setTeam('');
    setLoadedTemplateId(null);
  };

  // Build the editable-field list from a raw analysis (mirrors the backend's
  // /tickets/analyze/ processing) so a cached-template load needs no server call.
  const buildEditableFields = (analysisObj) => {
    const fields = (analysisObj && analysisObj.fields) || [];
    const skip = ['other', 'barcode', 'status', 'member_info'];
    const out = [];
    fields.forEach((f) => {
      const cat = f.category || 'other';
      if (skip.includes(cat)) return;
      const cur = f.text || '';
      out.push({
        category: cat,
        label: cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        current_value: cur,
        auto_value: f.replacement || cur,
        bbox: f.bbox,
        font_size: f.font_size,
      });
    });
    return out;
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    setImageFile(file);
    resetState();
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  }, []);

  const analyzeTicket = async () => {
    if (!imageFile) return;
    setAnalyzing(true);
    setError(null);

    const formData = new FormData();
    formData.append('image', imageFile);

    try {
      const res = await axiosInstance.post('/tickets/analyze/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      const { editable_fields, detected_team, game_info } = res.data;
      setAnalysis(res.data.analysis);
      setEditableFields(editable_fields);
      setTeam(detected_team || '');

      if (autoLookup) {
        setGameInfo(game_info);
      } else {
        setGameInfo(null);
      }

      const values = {};
      editable_fields.forEach((f) => {
        values[f.category] = autoLookup
          ? (f.auto_value || f.current_value || '')
          : (f.current_value || '');
      });
      setFieldValues(values);
      // Seed the seats input from whatever seat we read off the uploaded ticket.
      if (values.seat) setSeatsInput(values.seat);
    } catch (err) {
      const type = classifyError(err);
      setError(errorMessage(type, err));
    } finally {
      setAnalyzing(false);
    }
  };

  // Auto-analyze after file is selected
  useEffect(() => {
    if (imageFile && !analysis) {
      analyzeTicket();
    }
  }, [imageFile]);

  const searchGame = async () => {
    if (!team) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/tickets/search-game/', {
        params: { team, event_type: eventType },
      });
      setGameInfo(res.data.game_info);

      const gi = res.data.game_info;
      setFieldValues((prev) => {
        const updated = { ...prev };
        editableFields.forEach((f) => {
          if (f.category === 'home_team') updated.home_team = gi.home_team.name;
          if (f.category === 'away_team') updated.away_team = gi.away_team.name;
          if (f.category === 'event_name') updated.event_name = `${gi.away_team.name} vs ${gi.home_team.name}`;
          if (f.category === 'date') updated.date = gi.date;
          if (f.category === 'time') updated.time = gi.time;
          if (f.category === 'venue') updated.venue = gi.venue;
        });
        return updated;
      });
    } catch (err) {
      const type = classifyError(err);
      setError(errorMessage(type, err));
    } finally {
      setLoading(false);
    }
  };

  const updateResult = (id, patch) => {
    setResults((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // Build the set of values this ticket is supposed to contain, used both to
  // drive generation and to verify the result against.
  const buildExpected = (seatVal) => {
    const exp = {};
    ['section', 'row', 'seat', 'gate', 'date', 'time', 'venue', 'matchup', 'event_name'].forEach((k) => {
      if (fieldValues[k]) exp[k] = fieldValues[k];
    });
    if (seatVal) exp.seat = seatVal;
    return exp;
  };

  const genRequest = async (seatVal, corrective) => {
    const fd = new FormData();
    fd.append('image', imageFile);
    fd.append('team', team);
    fd.append('event_type', eventType);
    Object.entries(fieldValues).forEach(([k, v]) => {
      if (v) fd.append(k, v);
    });
    // Per-ticket seat wins over the analyzed one.
    if (seatVal) fd.set('seat', seatVal);
    if (corrective) fd.append('corrective', corrective);
    const res = await axiosInstance.post('/tickets/generate/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 150000,
    });
    return res.data; // { image, game_info, expected }
  };

  const verifyRequest = async (imageB64, expected) => {
    const res = await axiosInstance.post(
      '/tickets/verify/',
      { image: imageB64, expected },
      { timeout: 70000 }
    );
    return res.data.verification;
  };

  // Deterministic path: paint seat/section/row directly (pixel-exact, no AI).
  // Returns { image, replaced, skipped } — `skipped` lists changed fields it
  // couldn't do (long lines / busy background) so we know when to fall back.
  const compositeRequest = async (seatVal) => {
    const fd = new FormData();
    fd.append('image', imageFile);
    fd.append('analysis', JSON.stringify(analysis || {}));
    Object.entries(fieldValues).forEach(([k, v]) => {
      if (v) fd.append(k, v);
    });
    if (seatVal) fd.set('seat', seatVal);
    const res = await axiosInstance.post('/tickets/composite/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 45000,
    });
    return res.data;
  };

  // Generate one ticket for one seat, then run the "make it more perfect" check:
  // read the result back, and if a seating value is wrong (or it looks garbled)
  // regenerate once with the exact correction. Updates the card live throughout.
  // Rank a verification so we can keep the BEST attempt, never a worse refine:
  // exact > seating-correct > (couldn't verify) > seating-wrong.
  const scoreOf = (v) => (!v ? 1 : v.matches ? 3 : v.critical_ok ? 2 : 0);

  // Wrap a generation call with automatic backoff on transient failures (busy
  // image service / dropped signal), so a blip doesn't dead-end the ticket. The
  // card shows a live "auto-retrying in Ns" countdown; only a non-transient
  // error or exhausted retries throws to the caller.
  const genWithRetry = async (id, seatVal, corrective, phase) => {
    let auto = 0;
    for (;;) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await genRequest(seatVal, corrective);
      } catch (err) {
        const type = classifyError(err);
        if (!RETRYABLE.includes(type) || auto >= AUTO_RETRIES) throw err;
        auto += 1;
        const label = type === 'rate_limit' ? 'Image service busy' : 'Connection issue';
        let wait = (type === 'rate_limit' ? 10 : 4) * auto;
        while (wait > 0) {
          updateResult(id, { status: 'retrying', retryNote: `${label} — auto-retrying in ${wait}s (${auto}/${AUTO_RETRIES})` });
          // eslint-disable-next-line no-await-in-loop
          await sleep(1000);
          wait -= 1;
        }
        updateResult(id, { status: phase, retryNote: null });
      }
    }
  };

  const runSeat = async (id, seatVal) => {
    const expected = buildExpected(seatVal);

    // A composite that changed *some* fields is still worth showing if the
    // generative path later dies (out of credits, upstream down). Better a
    // ticket with one stale field than an error and nothing.
    let partialComposite = null;

    // 1) Deterministic first: paint seat/section/row exactly with Pillow. If it
    // handles every field the user changed (the common case — seat numbers on a
    // solid panel), we're done: pixel-exact, instant, free, no AI, no verify.
    if (analysis && analysis.fields) {
      updateResult(id, { status: 'generating', startedAt: Date.now(), error: null, errorType: null });
      try {
        const comp = await compositeRequest(seatVal);
        if (comp && comp.image && (comp.replaced || []).length && (comp.skipped || []).length === 0) {
          updateResult(id, {
            status: 'done',
            image: `data:image/png;base64,${comp.image}`,
            verification: { matches: true, method: 'exact', checks: {}, artifacts: [] },
            gameInfo: null,
          });
          return;
        }
        if (comp && comp.image && (comp.replaced || []).length) {
          partialComposite = comp;
        }
        // Composite couldn't cover a changed long/complex field → fall through
        // to the generative path, which handles everything.
      } catch (e) {
        // Composite unavailable/failed → fall through to the generative path.
      }
    }

    // Show the partial composite rather than failing outright. Returns true if
    // there was something to show.
    const deliverPartial = () => {
      if (!partialComposite) return false;
      const skipped = partialComposite.skipped || [];
      updateResult(id, {
        status: 'warning',
        image: `data:image/png;base64,${partialComposite.image}`,
        verification: {
          matches: false,
          method: 'composite_partial',
          checks: {},
          artifacts: [],
          note: skipped.length
            ? `AI editing is unavailable, so ${skipped.join(', ')} still shows the original value.`
            : 'AI editing is unavailable — this is the deterministic edit only.',
        },
        gameInfo: null,
        error: null,
        errorType: null,
      });
      return true;
    };

    // 2) Generative fallback (AI edit + "make it more perfect" verify loop).
    let best = { img: null, verif: null, game: null, score: -1 };
    let prevVerif = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      updateResult(id, {
        status: attempt === 1 ? 'generating' : 'refining',
        attempt,
        error: null,
        errorType: null,
        ...(attempt === 1 ? { startedAt: Date.now() } : {}),
      });

      // Only correct actual wrong field values from the previous pass.
      const corrective = attempt > 1 && prevVerif && prevVerif.corrective
        ? prevVerif.corrective
        : null;

      let data;
      try {
        data = await genWithRetry(id, seatVal, corrective, attempt === 1 ? 'generating' : 'refining');
      } catch (err) {
        const type = classifyError(err);
        // A failed refine shouldn't throw away a good earlier attempt.
        if (best.img) {
          updateResult(id, {
            status: best.verif && !best.verif.matches && !best.verif.skipped ? 'warning' : 'done',
            image: `data:image/png;base64,${best.img}`,
            verification: best.verif,
            gameInfo: best.game,
          });
        } else if (!deliverPartial()) {
          updateResult(id, {
            status: FAILED.includes(type) ? type : 'error',
            errorType: type,
            error: errorMessage(type, err),
          });
        }
        return;
      }

      const imgDataUrl = `data:image/png;base64,${data.image}`;
      // Show the fresh image while the "making it more perfect" check runs.
      updateResult(id, { status: 'verifying', attempt, image: imgDataUrl, gameInfo: data.game_info });

      const expForVerify = data.expected && Object.keys(data.expected).length
        ? data.expected
        : expected;
      let verif = null;
      try {
        verif = await verifyRequest(data.image, expForVerify);
      } catch (e) {
        verif = null; // verification unavailable — don't block delivering the ticket
      }
      prevVerif = verif;

      const sc = scoreOf(verif);
      if (sc > best.score) {
        best = { img: data.image, verif, game: data.game_info, score: sc };
      }

      // Refine only for a wrong seating value; a correct (or unverifiable)
      // ticket stops here so we never regenerate a good one into a worse one.
      if (!verif || !verif.should_refine) break;
    }

    updateResult(id, {
      status: best.verif && !best.verif.matches && !best.verif.skipped ? 'warning' : 'done',
      image: `data:image/png;base64,${best.img}`,
      verification: best.verif,
      gameInfo: best.game,
      error: null,
      errorType: null,
    });
  };

  const generateTickets = async () => {
    if (!imageFile) {
      setError('Need a ticket image.');
      return;
    }
    if (autoLookup && !team) {
      setError('Enter a team name (or set league to "None" for a custom ticket).');
      return;
    }

    const parsed = parseSeats(seatsInput);
    const seatList = parsed.length ? parsed : [fieldValues.seat || ''];
    setError(null);
    setDownloadStatus(null);
    setBatchRunning(true);

    const base = Date.now();
    const initial = seatList.map((s, i) => ({
      id: `${base}-${i}`,
      seat: s,
      status: 'queued',
    }));
    setResults(initial);
    // Jump the results into view so tapping Generate always shows something is
    // happening — otherwise on a phone the spinner is below the fold.
    setTimeout(() => {
      if (resultsRef.current) resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    // Sequential on purpose: one bounded request at a time is far more reliable
    // on stadium cell/wifi than firing a dozen slow image calls at once, and the
    // user watches them fill in one by one.
    for (let i = 0; i < initial.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await runSeat(initial[i].id, initial[i].seat);
    }
    setBatchRunning(false);
  };

  const retrySeat = async (id) => {
    const r = results.find((x) => x.id === id);
    if (!r) return;
    await runSeat(id, r.seat);
  };

  const dataUrlToBlob = (dataUrl) => {
    const [meta, b64] = dataUrl.split(',');
    const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/png';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const ticketFilenameFor = (seat) =>
    `ticket-${(team || 'custom').replace(/\s+/g, '-').toLowerCase()}${seat ? `-seat${seat}` : ''}.png`;

  // Single-ticket download uses the native Save dialog when available so the
  // user sees exactly where it landed; falls back to the Downloads folder.
  const downloadOne = async (r) => {
    const name = ticketFilenameFor(r.seat);
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: name,
          types: [{ description: 'PNG image', accept: { 'image/png': ['.png'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(dataUrlToBlob(r.image));
        await writable.close();
        updateResult(r.id, { note: `Saved as "${handle.name}".` });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    const link = document.createElement('a');
    link.href = r.image;
    link.download = name;
    link.click();
    updateResult(r.id, { note: `Saved to Downloads as "${name}".` });
  };

  // "Download all" can't open a Save dialog per file, so it goes straight to the
  // Downloads folder with a small gap so the browser doesn't drop them.
  const downloadAll = async () => {
    const ready = results.filter((r) => r.image && (r.status === 'done' || r.status === 'warning'));
    for (let i = 0; i < ready.length; i++) {
      const link = document.createElement('a');
      link.href = ready[i].image;
      link.download = ticketFilenameFor(ready[i].seat);
      link.click();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 400));
    }
    setDownloadStatus(`Downloaded ${ready.length} ticket${ready.length === 1 ? '' : 's'} to your Downloads folder.`);
  };

  const openTicketInNewTab = (r) => {
    if (!r.image) return;
    const url = URL.createObjectURL(dataUrlToBlob(r.image));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const loadTemplate = async (template) => {
    resetState();
    setTeam(template.team || '');
    setEventType(template.event_type || 'mlb');
    try {
      const response = await fetch(template.image_url);
      const blob = await response.blob();
      const file = new File([blob], `${template.name}.jpg`, { type: blob.type });
      setImagePreview(template.image_url);

      // If this template has CACHED boxes, reuse them instantly — no model call,
      // deterministic paint. (Set analysis before imageFile so the auto-analyze
      // effect, which only runs when analysis is empty, stays off.)
      if (template.analysis && template.analysis.fields) {
        const ef = buildEditableFields(template.analysis);
        setAnalysis(template.analysis);
        setEditableFields(ef);
        const vals = {};
        ef.forEach((f) => { vals[f.category] = f.current_value || ''; });
        setFieldValues(vals);
        if (vals.seat) setSeatsInput(vals.seat);
        setLoadedTemplateId(template.id);
      }
      setImageFile(file);
    } catch (e) {
      setError('Failed to load template image.');
    }
  };

  // Re-read a saved template's image with the vision model and re-cache the
  // boxes — the "redo it if it came out wrong" action.
  const rescanTemplate = async () => {
    if (!loadedTemplateId) return;
    setRescanning(true);
    setError(null);
    try {
      const res = await axiosInstance.post(`ticket-templates/${loadedTemplateId}/reanalyze/`, {}, { timeout: 180000 });
      const tpl = res.data;
      if (tpl.analysis && tpl.analysis.fields) {
        const ef = buildEditableFields(tpl.analysis);
        setAnalysis(tpl.analysis);
        setEditableFields(ef);
        const vals = {};
        ef.forEach((f) => { vals[f.category] = f.current_value || ''; });
        setFieldValues(vals);
        if (vals.seat) setSeatsInput(vals.seat);
      }
      fetchTemplates();
    } catch (e) {
      setError(errorMessage(classifyError(e), e));
    } finally {
      setRescanning(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!imageFile || !saveName) return;
    const formData = new FormData();
    formData.append('name', saveName);
    formData.append('image', imageFile);
    formData.append('team', team);
    formData.append('event_type', eventType);
    Object.entries(fieldValues).forEach(([key, val]) => {
      if (val) formData.append(key, val);
    });
    // Cache the boxes on the template so future loads need no model call.
    if (analysis) formData.append('analysis', JSON.stringify(analysis));
    try {
      await axiosInstance.post('ticket-templates/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSaveName('');
      setShowSaveForm(false);
      fetchTemplates();
    } catch (e) {
      setError('Failed to save template.');
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axiosInstance.delete(`ticket-templates/${id}/`);
      fetchTemplates();
    } catch (e) {}
  };

  const updateFieldValue = (category, value) => {
    setFieldValues((prev) => ({ ...prev, [category]: value }));
  };

  // ---- per-card status presentation ----
  const elapsedFor = (r) => (r.startedAt ? Math.max(0, Math.floor((nowTick - r.startedAt) / 1000)) : 0);

  const statusChip = (r) => {
    if (r.status === 'generating') return { color: 'yellow', icon: 'circle notch', text: `Generating… ${elapsedFor(r)}s` };
    if (r.status === 'verifying') return { color: 'blue', icon: 'circle notch', text: 'Making it more perfect…' };
    if (r.status === 'refining') return { color: 'blue', icon: 'circle notch', text: `Making it more perfect… ${elapsedFor(r)}s` };
    if (r.status === 'retrying') return { color: 'orange', icon: 'circle notch', text: 'Busy — auto-retrying…' };
    if (r.status === 'queued') return { color: 'grey', icon: 'clock outline', text: 'Queued' };
    if (r.status === 'done') {
      if (r.verification && r.verification.method === 'exact') return { color: 'green', icon: 'check circle', text: 'Exact ✓' };
      if (r.verification && r.verification.matches) return { color: 'green', icon: 'check circle', text: 'Verified exact' };
      if (r.verification && r.verification.skipped) return { color: 'green', icon: 'check', text: 'Ready' };
      return { color: 'olive', icon: 'check', text: "Ready (couldn't auto-verify)" };
    }
    if (r.status === 'warning') return { color: 'orange', icon: 'exclamation triangle', text: 'Check details' };
    // failure states
    if (r.status === 'timeout') return { color: 'red', icon: 'clock', text: 'Timed out' };
    if (r.status === 'network') return { color: 'red', icon: 'wifi', text: 'No connection' };
    if (r.status === 'rate_limit') return { color: 'red', icon: 'hourglass half', text: 'Service busy' };
    return { color: 'red', icon: 'times circle', text: 'Failed' };
  };

  const mismatchLines = (v) => {
    if (!v || !v.checks) return [];
    return Object.entries(v.checks)
      .filter(([, c]) => !c.ok)
      .map(([k, c]) => `${k}: wanted “${c.expected}”${c.found ? `, got “${c.found}”` : ' (not found)'}`)
      .concat((v.artifacts || []).map((a) => `render: ${a}`));
  };

  const isSeatField = (cat) => cat === 'seat';

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>

      {/* First-time instructions */}
      {showHelp ? (
        <Segment inverted style={{ backgroundColor: 'rgba(216,172,73,0.12)', border: '1px solid #d8ac49', marginBottom: '14px', padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ color: '#d8ac49', fontWeight: 'bold' }}>
              <Icon name="info circle" /> How to make tickets
            </span>
            <Icon name="close" link style={{ color: '#aaa' }} onClick={dismissHelp} title="Dismiss" />
          </div>
          <ol style={{ color: '#ddd', fontSize: '0.85em', margin: 0, paddingLeft: '18px', lineHeight: 1.6 }}>
            <li>Pick a <strong>League</strong> (or “None” to fill everything in yourself).</li>
            <li><strong>Upload a photo</strong> of a ticket to use as the template.</li>
            <li>
              <strong style={{ color: '#d8ac49' }}>Update every field</strong> below. The values shown are just what we
              read from your image — edit them to match your game/seats.
            </li>
            <li>
              For a group, put <strong>multiple seats</strong> in the Seats box —
              e.g. <code>12, 13, 15-18</code> makes one ticket each.
            </li>
            <li>Click <strong>Generate</strong>. We generate each ticket, then double-check it matches before showing it.</li>
          </ol>
        </Segment>
      ) : (
        <div style={{ textAlign: 'right', marginBottom: '8px' }}>
          <span style={{ color: '#888', fontSize: '0.8em', cursor: 'pointer' }}
            onClick={() => setShowHelp(true)}>
            <Icon name="question circle outline" /> How it works
          </span>
        </div>
      )}

      {/* Saved Templates */}
      {templates.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{ color: '#d8ac49', margin: '0 0 8px 0' }}>Saved Templates</h4>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px' }}>
            {templates.map((t) => (
              <div
                key={t.id}
                onClick={() => loadTemplate(t)}
                style={{
                  minWidth: '100px', cursor: 'pointer', border: '1px solid #555',
                  borderRadius: '6px', padding: '6px', background: 'rgba(0,0,0,0.3)',
                  textAlign: 'center', position: 'relative',
                }}
              >
                <img src={t.image_url} alt={t.name}
                  style={{ width: '80px', height: '50px', objectFit: 'cover', borderRadius: '3px' }} />
                <div style={{ color: '#ccc', fontSize: '0.75em', marginTop: '3px' }}>{t.name}</div>
                <div style={{ color: '#888', fontSize: '0.65em' }}>{t.team}</div>
                <Icon name="trash" size="small"
                  style={{ position: 'absolute', top: '3px', right: '3px', color: '#888', cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* League selector - pick this BEFORE uploading. Default "None". */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ color: '#ccc', display: 'block', marginBottom: '4px', fontSize: '0.9em' }}>
          League
        </label>
        <Dropdown selection fluid options={EVENT_TYPES} value={eventType}
          onChange={(e, { value }) => setEventType(value)}
          style={{ backgroundColor: '#333', color: '#fff' }} />
        <div style={{ color: '#888', fontSize: '0.75em', marginTop: '4px' }}>
          {autoLookup
            ? "Auto-fills today's real game for the team you enter below."
            : 'Custom ticket — no game lookup. You edit every field yourself.'}
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => document.getElementById('ticket-upload-input').click()}
        style={{
          border: `2px dashed ${dragOver ? '#d8ac49' : '#555'}`,
          borderRadius: '8px', padding: imagePreview ? '10px' : '30px',
          textAlign: 'center', cursor: 'pointer',
          backgroundColor: dragOver ? 'rgba(216,172,73,0.1)' : 'rgba(0,0,0,0.2)',
          marginBottom: '12px',
        }}
      >
        {imagePreview ? (
          <img src={imagePreview} alt="Uploaded ticket"
            style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '4px' }} />
        ) : (
          <div>
            <Icon name="cloud upload" size="big" style={{ color: '#888' }} />
            <p style={{ color: '#aaa', marginTop: '8px', marginBottom: 0 }}>
              Drop a ticket image here or click to upload
            </p>
          </div>
        )}
        <input id="ticket-upload-input" type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files[0])} />
      </div>

      {/* Analyzing indicator */}
      {analyzing && (
        <Segment inverted loading style={{ backgroundColor: 'rgba(0,0,0,0.4)', minHeight: '60px' }}>
          <p style={{ color: '#d8ac49' }}>Analyzing ticket fields...</p>
        </Segment>
      )}

      {/* Save as Template */}
      {imageFile && !analyzing && (
        <div style={{ marginBottom: '10px' }}>
          {!showSaveForm ? (
            <Button basic inverted size="tiny" onClick={() => setShowSaveForm(true)}>
              <Icon name="save" /> Save as Template
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: '6px' }}>
              <Input size="small" placeholder="Template name..." value={saveName}
                onChange={(e) => setSaveName(e.target.value)} style={{ flex: 1 }} />
              <Button color="yellow" size="small" onClick={saveAsTemplate} disabled={!saveName}>Save</Button>
              <Button basic inverted size="small" onClick={() => setShowSaveForm(false)}>Cancel</Button>
            </div>
          )}
        </div>
      )}

      {/* Dynamic Fields - only show after analysis */}
      {editableFields.length > 0 && !analyzing && (
        <Form inverted size="small">
          <Segment inverted style={{ backgroundColor: 'rgba(216,172,73,0.12)', border: '1px solid #d8ac49', padding: '8px 12px', marginBottom: '12px' }}>
            <span style={{ color: '#d8ac49', fontSize: '0.85em' }}>
              <Icon name="edit outline" /> <strong>Update these fields</strong> to match the ticket you want.
              These values were read from your uploaded image — change any that aren’t right before generating.
            </span>
          </Segment>

          {/* Cached-template banner + re-scan (redo the read if boxes are wrong) */}
          {loadedTemplateId && (
            <Segment inverted style={{ backgroundColor: 'rgba(122,199,122,0.10)', border: '1px solid #4c7a4c', padding: '8px 12px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#7ac77a', fontSize: '0.82em' }}>
                <Icon name="bolt" /> Using this template’s <strong>saved field boxes</strong> — instant &amp; free, no model call.
                {' '}If the placement looks off, re-scan it.
              </span>
              <Button basic inverted size="mini" loading={rescanning} disabled={rescanning}
                onClick={rescanTemplate} style={{ whiteSpace: 'nowrap' }}>
                <Icon name="redo" /> Re-scan boxes
              </Button>
            </Segment>
          )}

          {/* Team search row - only for leagues we auto-look up */}
          {autoLookup && (
            <Form.Group widths="equal">
              <Form.Field>
                <label style={{ color: '#ccc' }}>Team ({eventType.toUpperCase()})</label>
                <Input placeholder="e.g. Red Sox, Padres..."
                  value={team} onChange={(e) => setTeam(e.target.value)}
                  action={{ icon: 'search', color: 'yellow', onClick: searchGame, loading }} />
              </Form.Field>
            </Form.Group>
          )}

          {/* Dynamic fields from AI analysis (seat handled separately below) */}
          <Form.Group widths="equal">
            {editableFields.filter((f) => !isSeatField(f.category)).map((f, idx) => (
              <Form.Field key={`${f.category}-${idx}`}>
                <label style={{ color: '#ccc' }}>{f.label}</label>
                <Input
                  placeholder={f.current_value}
                  value={fieldValues[f.category] || ''}
                  onChange={(e) => updateFieldValue(f.category, e.target.value)}
                  size="small"
                />
              </Form.Field>
            ))}
          </Form.Group>

          {/* Seats — one or many. This is the batch input. */}
          <Form.Field>
            <label style={{ color: '#ccc' }}>
              Seats <span style={{ color: '#888', fontWeight: 'normal' }}>— one or several (e.g. 12, 13, 15-18)</span>
            </label>
            <Input
              placeholder="e.g. 14  or  12, 13, 15-18"
              value={seatsInput}
              onChange={(e) => setSeatsInput(e.target.value)}
              size="small"
              icon="ticket"
              iconPosition="left"
            />
            {(() => {
              const n = parseSeats(seatsInput).length;
              if (n > 1) return (
                <div style={{ color: '#7ac1e0', fontSize: '0.75em', marginTop: '4px' }}>
                  <Icon name="clone" /> {n} tickets will be generated — one per seat.
                </div>
              );
              return (
                <div style={{ color: '#888', fontSize: '0.75em', marginTop: '4px' }}>
                  Leave a single number for one ticket, or list several for a group.
                </div>
              );
            })()}
          </Form.Field>

          {gameInfo && (
            <Segment inverted style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '8px 12px', marginBottom: '10px' }}>
              <span style={{ color: '#d8ac49' }}>{gameInfo.event_name}</span>
              <span style={{ color: '#aaa', marginLeft: '10px' }}>{gameInfo.date} {gameInfo.time}</span>
            </Segment>
          )}

          <Button color="yellow" fluid loading={batchRunning}
            disabled={batchRunning || !imageFile || (autoLookup && !team)} onClick={generateTickets}>
            <Icon name="magic" /> {parseSeats(seatsInput).length > 1 ? `Generate ${parseSeats(seatsInput).length} Tickets` : 'Generate Ticket'}
          </Button>
          <div style={{ color: '#888', fontSize: '0.72em', textAlign: 'center', marginTop: '6px' }}>
            Each ticket is generated, then auto-checked against your details before it’s shown.
          </div>

          {/* Immediate top-level feedback the moment Generate is tapped, so it
              never looks like nothing happened (the grid is below the fold). */}
          {batchRunning && (
            <Segment inverted style={{ background: 'rgba(216,172,73,0.14)', border: '1px solid #d8ac49', marginTop: '10px', padding: '10px 12px', textAlign: 'center' }}>
              <Icon name="circle notch" loading style={{ color: '#d8ac49' }} />
              <span style={{ color: '#d8ac49', fontWeight: 'bold', marginLeft: '6px' }}>
                Working on your {results.length > 1 ? `${results.length} tickets` : 'ticket'}…
              </span>
              <div style={{ color: '#bbb', fontSize: '0.78em', marginTop: '4px' }}>
                {results.filter((r) => !ACTIVE.includes(r.status)).length}/{results.length} done · each takes ~45s. Results appear below.
              </div>
            </Segment>
          )}
        </Form>
      )}

      {error && (
        <Message negative style={{ marginTop: '12px' }}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* scroll target so tapping Generate always brings the results into view */}
      <div ref={resultsRef} />

      {/* Results grid */}
      {results.length > 0 && (
        <Segment inverted style={{ backgroundColor: 'rgba(0,0,0,0.4)', marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ color: '#d8ac49', margin: 0 }}>
              {results.length > 1 ? `Tickets (${results.length})` : 'Generated Ticket'}
            </h4>
            {anyReady && (
              <Button size="tiny" color="green" onClick={downloadAll} disabled={batchRunning}>
                <Icon name="download" /> Download all
              </Button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
            {results.map((r) => {
              const chip = statusChip(r);
              const failed = FAILED.includes(r.status);
              const active = ACTIVE.includes(r.status);
              return (
                <div key={r.id} style={{
                  border: '1px solid #444', borderRadius: '6px', padding: '8px',
                  background: 'rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ color: '#ccc', fontSize: '0.8em', fontWeight: 'bold' }}>
                      {r.seat ? `Seat ${r.seat}` : 'Ticket'}
                    </span>
                    <Label color={chip.color} size="mini">
                      <Icon name={chip.icon} loading={active} /> {chip.text}
                    </Label>
                  </div>

                  {r.image ? (
                    <img
                      src={r.image}
                      alt={r.seat ? `Seat ${r.seat}` : 'Ticket'}
                      style={{
                        width: '100%', borderRadius: '4px', border: '1px solid #444',
                        cursor: 'pointer', opacity: active ? 0.6 : 1,
                      }}
                      onClick={() => {
                        props.setSelectedTicket({ image: r.image, gameInfo: r.gameInfo });
                        props.setFullscreen('dijon-tickets');
                      }}
                    />
                  ) : (
                    <div style={{
                      height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#777', fontSize: '0.8em', border: '1px dashed #444', borderRadius: '4px',
                    }}>
                      {failed ? 'No image' : 'Working…'}
                    </div>
                  )}

                  {/* verification / error detail */}
                  {r.status === 'retrying' && r.retryNote && (
                    <div style={{ color: '#e0a94a', fontSize: '0.72em', marginTop: '6px' }}>
                      <Icon name="hourglass half" /> {r.retryNote}
                    </div>
                  )}
                  {r.status === 'warning' && (
                    <div style={{ color: '#e0a94a', fontSize: '0.72em', marginTop: '6px', lineHeight: 1.4 }}>
                      <Icon name="exclamation triangle" /> Couldn’t confirm every field:
                      <ul style={{ margin: '3px 0 0', paddingLeft: '16px' }}>
                        {mismatchLines(r.verification).map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.status === 'done' && r.verification && r.verification.method === 'exact' && (
                    <div style={{ color: '#7ac77a', fontSize: '0.72em', marginTop: '6px' }}>
                      <Icon name="check circle" /> Placed exactly — no AI, pixel-for-pixel from your ticket.
                    </div>
                  )}
                  {r.status === 'done' && r.verification && r.verification.matches && r.verification.method !== 'exact' && (
                    <div style={{ color: '#7ac77a', fontSize: '0.72em', marginTop: '6px' }}>
                      <Icon name="check circle" /> Seat, section & details verified against your input.
                    </div>
                  )}
                  {failed && (
                    <div style={{ color: '#e07a7a', fontSize: '0.72em', marginTop: '6px', lineHeight: 1.4 }}>
                      {r.error}
                    </div>
                  )}
                  {r.note && (
                    <div style={{ color: '#7ac77a', fontSize: '0.7em', marginTop: '4px' }}>
                      <Icon name="check" /> {r.note}
                    </div>
                  )}

                  {/* actions */}
                  <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {r.image && !active && (
                      <>
                        <Button size="mini" color="green" onClick={() => downloadOne(r)} style={{ padding: '6px 8px' }}>
                          <Icon name="download" /> Save
                        </Button>
                        <Button size="mini" color="teal" onClick={() => openTicketInNewTab(r)} style={{ padding: '6px 8px' }}>
                          <Icon name="external" /> Open
                        </Button>
                      </>
                    )}
                    {(failed || r.status === 'warning') && !active && (
                      <Button size="mini" color="yellow" onClick={() => retrySeat(r.id)} disabled={batchRunning} style={{ padding: '6px 8px' }}>
                        <Icon name="redo" /> Retry
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {downloadStatus && (
            <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#7ac77a' }}>
              <Icon name="check circle" /> {downloadStatus}
            </div>
          )}
        </Segment>
      )}
    </div>
  );
}

export default Tickets;
